import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  CircularProgress,
  Autocomplete,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { it } from 'date-fns/locale';
import { useForm, Controller } from 'react-hook-form';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { createTask } from '../../services/api/tasks/tasks';
import { getLeadsUnderManagement } from '../../services/api/lead/lead';
import { toast } from 'react-toastify';
import { Lead } from '@tassomutuo-backoffice/types';

interface AddTaskModalProps {
  open: boolean;
  onClose: () => void;
  initialDate?: Date;
  lead?: Lead; // Made optional
}

interface TaskFormData {
  date: Date;
  action: string;
  notes: string;
  lead?: Lead; // Add lead to form data
}

// Helper function to get the next hour rounded
const getNextHour = (): Date => {
  const date = new Date();
  date.setHours(date.getHours() + 1);
  date.setMinutes(0);
  date.setSeconds(0);
  date.setMilliseconds(0);
  return date;
};

const AddTaskModal: React.FC<AddTaskModalProps> = ({
  open,
  onClose,
  initialDate,
  lead,
}) => {
  const queryClient = useQueryClient();
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [leadError, setLeadError] = useState<string>('');
  const showLeadSelector = !lead; // Show lead selector only when lead is not provided

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<TaskFormData>({
    defaultValues: {
      date: initialDate || getNextHour(),
      action: '',
      notes: '',
      lead: undefined,
    },
  });

  // Query to fetch leads under management (only when needed)
  const { data: leadsData, isLoading: isLoadingLeads } = useQuery({
    queryKey: ['leadsUnderManagement'],
    queryFn: getLeadsUnderManagement,
    enabled: showLeadSelector && open, // Only fetch when selector is shown and modal is open
    refetchOnWindowFocus: false,
  });

  const leads = leadsData?.results || [];

  // Update form when initialDate or modal opens
  useEffect(() => {
    if (open) {
      reset({
        date: initialDate || getNextHour(),
        action: '',
        notes: '',
        lead: undefined,
      });
      setSelectedLead(null);
      setLeadError('');
    }
  }, [open, initialDate, reset]);

  // Create task mutation
  const createTaskMutation = useMutation({
    mutationFn: (taskData: {
      lead: Lead;
      date: string;
      action: string;
      notes?: string;
    }) => createTask(taskData),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['tasks'] });
      toast.success('Promemoria creato con successo');
      onClose();
    },
    onError: (error: Error) => {
      toast.error(
        error?.message || 'Errore durante la creazione del promemoria'
      );
      console.error('Error creating task:', error);
    },
  });

  const handleClose = () => {
    if (!isLoading) {
      reset();
      onClose();
    }
  };

  const onFormSubmit = (data: TaskFormData) => {
    const targetLead = lead || selectedLead;

    if (!targetLead) {
      toast.error('Seleziona una lead prima di creare il task');
      return;
    }

    createTaskMutation.mutate({
      lead: targetLead,
      date: data.date.toISOString(),
      action: data.action.trim(),
      notes: data.notes.trim() || undefined,
    });
  };

  const isLoading = createTaskMutation.isPending;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Aggiungi promemoria</DialogTitle>
      <DialogContent>
        <Box
          id="task-form"
          component="form"
          onSubmit={handleSubmit(onFormSubmit)}
          sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}
        >
          {showLeadSelector && (
            <Autocomplete
              options={leads}
              value={selectedLead}
              onChange={(_, newValue) => {
                setSelectedLead(newValue);
                setLeadError('');
              }}
              getOptionLabel={(option) => option.properties.dealname}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Box>
                    <Box sx={{ fontWeight: 500 }}>
                      {option.properties.dealname}
                    </Box>
                    <Box sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
                      {option.properties.phone_number}
                      {option.properties.email &&
                        ` • ${option.properties.email}`}
                    </Box>
                  </Box>
                </Box>
              )}
              loading={isLoadingLeads}
              disabled={isLoading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Lead"
                  placeholder="Cerca lead..."
                  required
                  error={!!leadError}
                  helperText={
                    leadError ||
                    'Seleziona la lead per cui creare il promemoria'
                  }
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isLoadingLeads ? <CircularProgress size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              noOptionsText="Nessuna lead trovata"
              isOptionEqualToValue={(option, value) => option.id === value.id}
            />
          )}

          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={it}>
            <Controller
              name="date"
              control={control}
              rules={{
                required: 'Data e ora sono obbligatorie',
                validate: (value) => {
                  if (!value) return 'Data e ora sono obbligatorie';
                  if (value < new Date()) {
                    return 'La data non può essere nel passato';
                  }
                  return true;
                },
              }}
              render={({ field }) => (
                <DateTimePicker
                  label="Data e Ora"
                  value={field.value}
                  onChange={field.onChange}
                  disabled={isLoading}
                  minDate={new Date()}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      required: true,
                      error: !!errors.date,
                      helperText: errors.date?.message,
                    },
                  }}
                />
              )}
            />
          </LocalizationProvider>

          <Controller
            name="action"
            control={control}
            rules={{
              required: 'Azione è obbligatoria',
              minLength: {
                value: 3,
                message: "L'azione deve contenere almeno 3 caratteri",
              },
              maxLength: {
                value: 500,
                message: "L'azione non può superare 500 caratteri",
              },
              validate: (value) =>
                value.trim().length >= 3 ||
                "L'azione deve contenere almeno 3 caratteri",
            }}
            render={({ field }) => (
              <TextField
                label="Azione"
                {...field}
                error={!!errors.action}
                helperText={errors.action?.message}
                fullWidth
                disabled={isLoading}
                multiline
                rows={2}
              />
            )}
          />

          <Controller
            name="notes"
            control={control}
            rules={{
              maxLength: {
                value: 1000,
                message: 'Le note non possono superare 1000 caratteri',
              },
            }}
            render={({ field }) => (
              <TextField
                label="Note"
                {...field}
                error={!!errors.notes}
                helperText={errors.notes?.message}
                fullWidth
                disabled={isLoading}
                multiline
                rows={3}
              />
            )}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isLoading}>
          Annulla
        </Button>
        <Button
          type="submit"
          form="task-form"
          variant="contained"
          disabled={isLoading}
          startIcon={isLoading ? <CircularProgress size={20} /> : null}
        >
          {isLoading ? 'Creazione...' : 'Crea Task'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddTaskModal;
